import React, { useState, useEffect } from 'react';
import { Play, Pause, RotateCcw, ChevronRight, ChevronLeft, AlertTriangle, CheckCircle2, Info, Zap } from 'lucide-react';

// ══════════════════════════════════════════════════════════════════════════
// PILOTE QUALITÉ · M4 · Démarrer & s'arrêter
// Œuvre pédagogique interactive — le geste fondamental du démarrage :
//  · 3 pédales frontales animées (embrayage/frein/accélérateur)
//  · Jauge régime moteur (tr/min) + jauge vitesse voiture
//  · Mini-voiture profil qui bouge ou cale selon la coordination
//  · Timeline 5 étapes cliquables avec explications riches
//  · Bouton "▶ Démarrer l'animation" qui joue la séquence automatiquement
// ══════════════════════════════════════════════════════════════════════════

// Chaque étape définit l'état des pédales + du moteur + de la voiture
const ETAPES = [
  {
    numero: 1,
    titre: "Débrayer & passer la 1ère",
    embrayage: 100, // % (100 = enfoncée à fond)
    frein: 0,
    acc: 0,
    rpm: 800,      // ralenti
    kmh: 0,
    voiture: 'immobile',
    dangerRating: 'ok', // ok | attention | danger
    gauche: "Pied gauche appuie À FOND sur l'embrayage — les disques sont totalement SÉPARÉS.",
    droite: "Main droite : passer la 1ère vitesse. Boîte au point mort → 1ère.",
    astuce: "Si tu essaies de passer une vitesse sans débrayer, tu vas entendre un CRAQUEMENT (les pignons refusent de s'engager). Toujours embrayage AVANT levier.",
  },
  {
    numero: 2,
    titre: "Regarder & annoncer",
    embrayage: 100,
    frein: 0,
    acc: 0,
    rpm: 800,
    kmh: 0,
    voiture: 'immobile',
    dangerRating: 'ok',
    gauche: "Pied gauche : embrayage toujours enfoncé.",
    droite: "REGARDER : rétroviseur intérieur → rétroviseur gauche → contrôle-épaule gauche. Puis clignotant gauche si départ depuis un stationnement.",
    astuce: "3 secondes suffisent pour un contrôle complet. C'est le geste que les jurys d'examen observent le plus attentivement.",
  },
  {
    numero: 3,
    titre: "🎯 Trouver le point de patinage",
    embrayage: 40, // remontée jusqu'au patinage
    frein: 0,
    acc: 0,
    rpm: 950,
    kmh: 1,
    voiture: 'patinage',
    dangerRating: 'attention',
    gauche: "Pied gauche : REMONTER l'embrayage TRÈS DOUCEMENT jusqu'à sentir que la voiture veut avancer. C'est LE point de patinage.",
    droite: "Pied droit : encore SUR le frein (par sécurité en cas de côte). Prêt à passer sur l'accélérateur.",
    astuce: "À ce moment précis, la voiture avance TOUTE SEULE au ralenti, sans accélérer. C'est le signal que l'embrayage transmet du couple.",
  },
  {
    numero: 4,
    titre: "Accélérer progressivement",
    embrayage: 20,
    frein: 0,
    acc: 15,
    rpm: 1500,
    kmh: 5,
    voiture: 'bouge',
    dangerRating: 'ok',
    gauche: "Pied gauche : continuer à RELÂCHER l'embrayage doucement (encore 2 cm).",
    droite: "Pied droit : glisser sur l'accélérateur et donner UN PEU de gaz (5-10% seulement).",
    astuce: "Le geste critique : coordination gauche/droite. Embrayage relâché SANS accélérateur = risque de calage. Trop d'accélérateur = moteur qui hurle sans avancer.",
  },
  {
    numero: 5,
    titre: "Rouler & lâcher l'embrayage",
    embrayage: 0,
    frein: 0,
    acc: 25,
    rpm: 2000,
    kmh: 15,
    voiture: 'bouge',
    dangerRating: 'ok',
    gauche: "Pied gauche : embrayage COMPLÈTEMENT relâché. Poser le pied au sol à GAUCHE de la pédale (pas dessus).",
    droite: "Pied droit : accélérer normalement pour prendre de la vitesse.",
    astuce: "Rouler avec le pied SUR la pédale d'embrayage = usure prématurée (patinage permanent). Coût de remplacement : 800-1500 €.",
  },
];

// ══════════════════════════════════════════════════════════════════════════
// Composants SVG internes
// ══════════════════════════════════════════════════════════════════════════

// Pédale vue de face (dessin détaillé) avec position animée
function Pedale({ type, pressed, label, x, y }) {
  // pressed 0-100 % : enfoncement
  const yOffset = pressed * 0.4; // décalage vertical selon appui
  const colors = {
    embrayage: '#dc2626',
    frein: '#f59e0b',
    accelerateur: '#22c55e',
  };
  const color = colors[type];
  return (
    <g transform={`translate(${x} ${y})`}>
      {/* Tige / support */}
      <rect x={-8} y={-5 + yOffset} width={16} height={40 - yOffset} fill="#374151" stroke="#1f2937" strokeWidth={1} />
      {/* Pédale (plaque) */}
      <g transform={`translate(0 ${35 + yOffset})`}>
        <rect x={-24} y={-6} width={48} height={12} rx={4} fill={color} stroke="#0b1120" strokeWidth={2} />
        <rect x={-20} y={-3} width={40} height={6} fill="none" stroke="#0b1120" strokeWidth={0.5} strokeDasharray="2 2" opacity={0.5} />
      </g>
      {/* Label */}
      <text x={0} y={90} textAnchor="middle" fill="#fff" fontSize={12} fontWeight="bold" fontFamily="system-ui">{label}</text>
      <text x={0} y={106} textAnchor="middle" fill="#94a3b8" fontSize={10} fontFamily="monospace">{pressed}%</text>
    </g>
  );
}

// Jauge circulaire (régime moteur, vitesse)
function Jauge({ x, y, value, max, label, unit, dangerZone = 0.8 }) {
  const pct = Math.min(1, value / max);
  const startAngle = -220; // -220° (en bas à gauche)
  const endAngle = 40;     // en bas à droite
  const angle = startAngle + (endAngle - startAngle) * pct;

  // Coordonnées de l'aiguille
  const rad = (angle * Math.PI) / 180;
  const needleLen = 34;
  const nx = Math.cos(rad) * needleLen;
  const ny = Math.sin(rad) * needleLen;

  const inDanger = pct >= dangerZone;

  return (
    <g transform={`translate(${x} ${y})`}>
      {/* Fond circulaire */}
      <circle r={45} fill="#0b1120" stroke="#374151" strokeWidth={2} />
      {/* Arc de graduation */}
      <path
        d={describeArc(0, 0, 38, startAngle, endAngle)}
        stroke="#334155"
        strokeWidth={5}
        fill="none"
      />
      {/* Zone danger (partie rouge) */}
      <path
        d={describeArc(0, 0, 38, startAngle + (endAngle - startAngle) * dangerZone, endAngle)}
        stroke="#dc2626"
        strokeWidth={5}
        fill="none"
      />
      {/* Aiguille */}
      <line x1={0} y1={0} x2={nx} y2={ny} stroke={inDanger ? '#ef4444' : '#fff'} strokeWidth={3} strokeLinecap="round" />
      <circle r={4} fill={inDanger ? '#ef4444' : '#fff'} />
      {/* Valeur */}
      <text y={20} textAnchor="middle" fill="#fff" fontSize={13} fontWeight="bold" fontFamily="monospace">{value}</text>
      <text y={32} textAnchor="middle" fill="#94a3b8" fontSize={9} fontFamily="monospace">{unit}</text>
      {/* Label */}
      <text y={-56} textAnchor="middle" fill="#94a3b8" fontSize={11} fontWeight="bold" fontFamily="system-ui">{label}</text>
    </g>
  );
}

// Helper pour dessiner un arc SVG
function describeArc(cx, cy, r, startAngle, endAngle) {
  const start = polarToCartesian(cx, cy, r, endAngle);
  const end = polarToCartesian(cx, cy, r, startAngle);
  const largeArc = endAngle - startAngle <= 180 ? '0' : '1';
  return `M ${start.x} ${start.y} A ${r} ${r} 0 ${largeArc} 0 ${end.x} ${end.y}`;
}
function polarToCartesian(cx, cy, r, angle) {
  const rad = (angle * Math.PI) / 180;
  return { x: cx + r * Math.cos(rad), y: cy + r * Math.sin(rad) };
}

// Mini voiture profil qui bouge/cale/patine
function MiniVoiture({ voiture, x, y }) {
  const animate = voiture === 'bouge' ? 'anim-move' : voiture === 'patinage' ? 'anim-shake' : '';
  return (
    <g transform={`translate(${x} ${y})`} className={animate}>
      <ellipse cx={0} cy={22} rx={62} ry={5} fill="#000" opacity={0.4} />
      {/* Carrosserie */}
      <path
        d="M-60 0 L-55 -20 L-25 -30 L 25 -30 L 45 -20 L 55 -18 L 60 -5 L 60 5 L-60 5 Z"
        fill="#dc2626"
        stroke="#0b1120"
        strokeWidth={2}
      />
      {/* Pare-brise et vitres */}
      <path d="M-24 -28 L-15 -18 L 30 -18 L 40 -28 Z" fill="#38bdf8" opacity={0.5} />
      {/* Phares */}
      <circle cx={54} cy={-8} r={4} fill="#fef3c7" />
      <circle cx={-54} cy={-8} r={3} fill="#dc2626" />
      {/* Roues */}
      <circle cx={-38} cy={8} r={13} fill="#0b1120" stroke="#374151" strokeWidth={2} />
      <circle cx={-38} cy={8} r={4} fill="#475569" />
      <circle cx={38} cy={8} r={13} fill="#0b1120" stroke="#374151" strokeWidth={2} />
      <circle cx={38} cy={8} r={4} fill="#475569" />
      {/* Fumée si patinage */}
      {voiture === 'patinage' && (
        <g opacity={0.5}>
          <circle cx={-50} cy={25} r={6} fill="#94a3b8">
            <animate attributeName="opacity" values="0.5;0;0.5" dur="1.2s" repeatCount="indefinite" />
          </circle>
          <circle cx={-45} cy={20} r={4} fill="#94a3b8">
            <animate attributeName="opacity" values="0.3;0;0.3" dur="1.5s" repeatCount="indefinite" />
          </circle>
        </g>
      )}
    </g>
  );
}

// ══════════════════════════════════════════════════════════════════════════
// Composant principal
// ══════════════════════════════════════════════════════════════════════════

export default function DemarrerSarreterSvg() {
  const [etape, setEtape] = useState(0);
  const [autoplay, setAutoplay] = useState(false);

  // Autoplay : boucle sur les 5 étapes toutes les 2.5s
  useEffect(() => {
    if (!autoplay) return undefined;
    const id = setInterval(() => {
      setEtape((e) => (e + 1) % ETAPES.length);
    }, 2500);
    return () => clearInterval(id);
  }, [autoplay]);

  const current = ETAPES[etape];

  return (
    <div className="w-full">
      {/* Barre d'outils */}
      <div className="mb-3 flex flex-wrap items-center gap-2">
        <button
          type="button"
          onClick={() => setAutoplay((a) => !a)}
          data-testid="m4-autoplay-toggle"
          className={`inline-flex items-center gap-1.5 h-9 px-4 rounded-full text-[12px] font-bold transition ${
            autoplay ? 'bg-emerald-600 hover:bg-emerald-500 text-white' : 'bg-red-600 hover:bg-red-500 text-white'
          }`}
        >
          {autoplay ? <><Pause size={13} /> Pause animation</> : <><Play size={13} /> Jouer la séquence</>}
        </button>
        <button
          type="button"
          onClick={() => { setEtape(0); setAutoplay(false); }}
          data-testid="m4-reset"
          className="inline-flex items-center gap-1.5 h-9 px-3 rounded-full border border-white/20 text-white/70 hover:bg-white/5 text-[12px] font-semibold"
        >
          <RotateCcw size={13} /> Reset
        </button>
        <span className="ml-auto text-white/40 text-[11px] font-mono-spec">Étape {current.numero} / {ETAPES.length}</span>
      </div>

      <div className="grid lg:grid-cols-[1fr,360px] gap-4">
        {/* Zone SVG principale */}
        <div className="rounded-2xl overflow-hidden border border-white/10 bg-gradient-to-b from-[#1a2434] to-[#0a1220]">
          <svg viewBox="0 0 700 620" className="w-full h-auto block max-h-[48vh] sm:max-h-[52vh]" role="img" aria-label="Démarrer et s'arrêter — pédales, jauges, voiture">
            <defs>
              <linearGradient id="m4-floor" x1="0" y1="0" x2="0" y2="1">
                <stop offset="0%" stopColor="#1e293b" />
                <stop offset="100%" stopColor="#0a1220" />
              </linearGradient>
            </defs>
            <rect width={700} height={620} fill="url(#m4-floor)" />

            {/* Titre section pédales */}
            <text x={175} y={40} textAnchor="middle" fill="#94a3b8" fontSize={12} fontWeight="bold" fontFamily="monospace">
              PÉDALES · position en temps réel
            </text>
            <rect x={40} y={60} width={270} height={200} rx={12} fill="#0f172a" stroke="#334155" strokeWidth={1} />
            <text x={175} y={80} textAnchor="middle" fill="#64748b" fontSize={9} fontFamily="monospace">Vue depuis le poste de conduite</text>

            {/* Les 3 pédales */}
            <Pedale type="embrayage"    pressed={current.embrayage} label="Embrayage"    x={90}  y={125} />
            <Pedale type="frein"        pressed={current.frein}     label="Frein"        x={175} y={125} />
            <Pedale type="accelerateur" pressed={current.acc}       label="Accélérateur" x={260} y={125} />

            {/* Section jauges */}
            <text x={510} y={40} textAnchor="middle" fill="#94a3b8" fontSize={12} fontWeight="bold" fontFamily="monospace">
              TABLEAU DE BORD
            </text>
            <rect x={350} y={60} width={310} height={200} rx={12} fill="#0f172a" stroke="#334155" strokeWidth={1} />
            <Jauge x={430} y={175} value={current.rpm}  max={4000} label="Régime moteur" unit="tr/min" dangerZone={0.85} />
            <Jauge x={580} y={175} value={current.kmh}  max={40}   label="Vitesse"       unit="km/h"   dangerZone={0.95} />

            {/* Séparateur */}
            <line x1={40} y1={295} x2={660} y2={295} stroke="#334155" strokeWidth={1} strokeDasharray="4 4" />

            {/* Section voiture */}
            <text x={350} y={325} textAnchor="middle" fill="#94a3b8" fontSize={12} fontWeight="bold" fontFamily="monospace">
              ÉTAT DE LA VOITURE
            </text>

            {/* Voiture animée */}
            <MiniVoiture voiture={current.voiture} x={350} y={410} />

            {/* Bulle statut */}
            <g transform="translate(350 490)">
              <rect x={-125} y={0} width={250} height={38} rx={19}
                fill={current.voiture === 'immobile' ? '#0f172a' : current.voiture === 'patinage' ? '#7f1d1d' : '#14532d'}
                stroke={current.voiture === 'immobile' ? '#475569' : current.voiture === 'patinage' ? '#dc2626' : '#22c55e'}
                strokeWidth={2}
              />
              <text x={0} y={24} textAnchor="middle" fill="#fff" fontSize={13} fontWeight="bold" fontFamily="system-ui">
                {current.voiture === 'immobile' ? '⏸ Voiture arrêtée' : current.voiture === 'patinage' ? '⚠ Point de patinage' : '▶ Voiture en mouvement'}
              </text>
            </g>

            {/* Titre bas */}
            <g transform="translate(0, 580)">
              <rect width={700} height={40} fill="#000" opacity={0.6} />
              <text x={20} y={26} fill="#fff" fontSize={14} fontWeight="bold">M4 · Démarrer & s&apos;arrêter</text>
              <text x={20} y={39} fill="#94a3b8" fontSize={11}>REMC C1a — Connaître les principales commandes</text>
              <circle cx={670} cy={20} r={16} fill="#dc2626" />
              <text x={670} y={25} textAnchor="middle" fill="#fff" fontSize={11} fontWeight="900">CV</text>
            </g>
          </svg>

          {/* Timeline étapes sous le SVG */}
          <div className="p-3 border-t border-white/10 bg-black/40">
            <div className="flex items-center gap-1.5">
              {ETAPES.map((e, i) => (
                <button
                  key={i}
                  type="button"
                  onClick={() => { setEtape(i); setAutoplay(false); }}
                  data-testid={`m4-step-${e.numero}`}
                  className={`flex-1 h-11 rounded-lg border text-[11px] font-bold transition flex flex-col items-center justify-center gap-0.5 ${
                    etape === i
                      ? 'border-red-500/70 bg-red-950/40 text-red-200 shadow-inner'
                      : 'border-white/10 bg-white/[0.02] text-white/60 hover:border-white/30 hover:bg-white/5'
                  }`}
                  aria-current={etape === i ? 'step' : undefined}
                >
                  <span className="text-[15px]">{e.numero}</span>
                  <span className="text-[9px] font-normal text-white/50 leading-tight px-1 truncate max-w-full">
                    {e.titre.replace(/^[🎯🔧🎬🏁▶]\s*/, '').substring(0, 18)}
                  </span>
                </button>
              ))}
            </div>
          </div>
        </div>

        {/* Panneau explicatif droit */}
        <div className="rounded-2xl border border-white/10 bg-[#0d0d0e] p-4 sm:p-5 self-start lg:max-h-[calc(100vh-300px)] lg:overflow-y-auto lg:custom-scroll" data-testid="m4-panel">
          <div className="flex items-start justify-between gap-2">
            <div>
              <div className="text-red-400 text-[10.5px] font-mono-spec uppercase tracking-wider font-bold">Étape {current.numero} / {ETAPES.length}</div>
              <h4 className="mt-1 font-serif-editorial text-white text-[19px] leading-tight">{current.titre}</h4>
            </div>
            <div className={`shrink-0 w-10 h-10 rounded-full flex items-center justify-center ${
              current.dangerRating === 'danger' ? 'bg-red-500/25 text-red-300'
              : current.dangerRating === 'attention' ? 'bg-amber-500/25 text-amber-300'
              : 'bg-emerald-500/25 text-emerald-300'
            }`}>
              {current.dangerRating === 'danger' ? <AlertTriangle size={16} /> : current.dangerRating === 'attention' ? <Zap size={16} /> : <CheckCircle2 size={16} />}
            </div>
          </div>

          <div className="mt-4 rounded-lg border border-red-500/40 bg-red-950/20 p-3">
            <div className="text-red-300 text-[10.5px] font-mono-spec uppercase tracking-wider font-bold">🦶 Pied GAUCHE</div>
            <p className="mt-1 text-white/85 text-[12.5px] leading-relaxed">{current.gauche}</p>
          </div>

          <div className="mt-3 rounded-lg border border-emerald-500/40 bg-emerald-950/20 p-3">
            <div className="text-emerald-300 text-[10.5px] font-mono-spec uppercase tracking-wider font-bold">🦶 Pied DROIT / Main</div>
            <p className="mt-1 text-white/85 text-[12.5px] leading-relaxed">{current.droite}</p>
          </div>

          <div className="mt-3 rounded-lg border border-cyan-500/40 bg-cyan-950/20 p-3 flex items-start gap-2">
            <Info size={14} className="text-cyan-300 mt-0.5 shrink-0" />
            <div>
              <div className="text-cyan-300 text-[10.5px] font-mono-spec uppercase tracking-wider font-bold">Astuce prof</div>
              <p className="mt-0.5 text-white/85 text-[12.5px] leading-relaxed">{current.astuce}</p>
            </div>
          </div>

          {/* Navigation */}
          <div className="mt-4 flex items-center justify-between gap-2">
            <button
              type="button"
              onClick={() => { setEtape((e) => (e - 1 + ETAPES.length) % ETAPES.length); setAutoplay(false); }}
              data-testid="m4-prev"
              className="inline-flex items-center gap-1 h-9 px-3 rounded-full border border-white/15 text-white/70 hover:bg-white/5 text-[12px]"
            >
              <ChevronLeft size={12} /> Précédente
            </button>
            <span className="text-white/40 text-[11px] font-mono-spec">{current.numero} / {ETAPES.length}</span>
            <button
              type="button"
              onClick={() => { setEtape((e) => (e + 1) % ETAPES.length); setAutoplay(false); }}
              data-testid="m4-next"
              className="inline-flex items-center gap-1 h-9 px-3 rounded-full border border-white/15 text-white/70 hover:bg-white/5 text-[12px]"
            >
              Suivante <ChevronRight size={12} />
            </button>
          </div>
        </div>
      </div>

      {/* CSS local pour les animations */}
      <style>{`
        .anim-move { animation: m4-move 1.5s ease-in-out infinite; }
        .anim-shake { animation: m4-shake 0.15s ease-in-out infinite; }
        @keyframes m4-move { 0%, 100% { transform: translate(350px, 410px); } 50% { transform: translate(360px, 410px); } }
        @keyframes m4-shake { 0%, 100% { transform: translate(350px, 410px); } 50% { transform: translate(351.5px, 411px); } }
      `}</style>
    </div>
  );
}
